import React from 'react';

/**
 * ScreenHeader — the editorial chapter heading used at the top of
 * Profile / Progress / Calendar / Learning / Privacy / Terms /
 * DailyReflection / ArticleDetail.
 *
 * Layout:
 *   EYEBROW               ← 11px Manrope, 0.32em tracking, caps
 *   Title                 ← 42px Fraunces italic opsz 96
 *
 * All three elements are optional except `title`. The wrapper carries
 * `motion-rise` so it staggers in at mount. Pass a different
 * `staggerIndex` if this block is not the first rising element on the
 * page (default 0).
 *
 * Variants:
 *   align   "left" (default) | "center"
 *   size    "default" (42px) | "compact" (34px) | "large" (48px)
 *   tone    "light" (default dark-brown) | "dark" (for dark backgrounds)
 *
 * Children render below the title as a subtitle slot — pass a
 * <p> with your own body copy. No subtitle is rendered by default.
 */
export default function ScreenHeader({
  eyebrow,
  title,
  subtitle,
  align = 'left',
  size = 'default',
  tone = 'light',
  staggerIndex = 0,
  children,
}) {
  const sizes = {
    compact: { fontSize: 34, lineHeight: 1.12 },
    default: { fontSize: 42, lineHeight: 1.08 },
    large: { fontSize: 52, lineHeight: 1.04 },
  };
  const tones = {
    light: { titleColor: 'var(--dark-brown)', eyebrowColor: 'var(--warm-brown)' },
    dark: { titleColor: '#ffffff', eyebrowColor: 'rgba(255,255,255,0.78)' },
  };
  const sz = sizes[size] || sizes.default;
  const tn = tones[tone] || tones.light;

  return (
    <div
      className="motion-rise"
      style={{
        textAlign: align,
        '--stagger-i': staggerIndex,
      }}
    >
      {eyebrow && (
        <div
          style={{
            fontFamily: 'var(--font-ui)',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.32em',
            textTransform: 'uppercase',
            color: tn.eyebrowColor,
            margin: '0 0 10px',
          }}
        >
          {eyebrow}
        </div>
      )}
      <h1
        style={{
          fontFamily: 'var(--font-title)',
          fontStyle: 'italic',
          fontWeight: 500,
          fontSize: sz.fontSize,
          lineHeight: sz.lineHeight,
          letterSpacing: '-0.02em',
          color: tn.titleColor,
          margin: 0,
          fontVariationSettings: '"opsz" 96',
        }}
      >
        {title}
      </h1>
      {subtitle && (
        <p
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 17,
            lineHeight: 1.6,
            color: tone === 'dark' ? 'rgba(255,255,255,0.78)' : 'var(--warm-brown)',
            margin: '18px 0 0',
            maxWidth: align === 'center' ? 360 : undefined,
          }}
        >
          {subtitle}
        </p>
      )}
      {children}
    </div>
  );
}
